import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  User,
  Wallet,
  Gift,
  TrendingDown,
  Users,
  Receipt,
  Pencil,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../../components/Tables/APITable";
import Notification from "../../../components/Notification/Notification";
import Loader from "../../../components/Loader/Loader";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";
import { getErrorMessage } from "../../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./AdminViewOrganization.module.scss";

/* =========================
   FORMATTERS
========================= */
const formatINR = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  }).format(Number(value));
};

const formatDate = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (iso) => {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminViewOrganization = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const showNotification = (message, type = "error") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  const fetchOrganization = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiGet("user", `/organizations/${id}`);
      if (res?.status === "success") {
        setOrganization(res.data || null);
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load organization",
          }),
        );
      }
    } catch (err) {
      showNotification(
        getErrorMessage(err, "Failed to load organization"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  if (loading) return <Loader />;

  if (!organization) {
    return (
      <div className={styles["view-org__page"]}>
        {notification && (
          <div className={styles["view-org__toast-container"]}>
            <Notification message={notification} type={notificationType} />
          </div>
        )}
        <div className={styles["view-org__header"]}>
          <Link to="/admin/organization" className={styles["view-org__back-btn"]}>
            <ArrowLeft size={20} strokeWidth={2.5} />
          </Link>
          <div className={styles["view-org__title-group"]}>
            <h2 className={styles["view-org__title"]}>Organization</h2>
            <p className={styles["view-org__subtitle"]}>
              Organization could not be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const credits = organization.credits || {};
  const lastPurchase = credits.lastPurchase || null;
  const isActive = organization.status?.toLowerCase() === "active";

  const contactFields = [
    { label: "Name", value: organization.name },
    { label: "Email", value: organization.email },
    { label: "Mobile", value: organization.mobile },
    { label: "Designation", value: organization.designation },
  ];

  const companyFields = [
    { label: "Company Name", value: organization.companyName },
    { label: "Industry", value: organization.industry },
    { label: "State", value: organization.state },
    { label: "Address", value: organization.address },
    { label: "GST Number", value: organization.gstNo },
    { label: "Sub Domain", value: organization.subDomain },
    {
      label: "User Limit",
      value: organization.unlimitedUsers
        ? "Unlimited"
        : formatNumber(organization.maxUsers),
    },
    {
      label: "Contract Signed",
      value: organization.contractSigned ? "Yes" : "No",
    },
  ];

  const billingFields = [
    {
      label: "Minimum Funds Value",
      value: formatINR(organization.minimumFundsValue),
    },
    { label: "Created On", value: formatDate(organization.createdAt) },
    { label: "Last Updated", value: formatDate(organization.updatedAt) },
  ];

  const userColumns = [
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Role", key: "role" },
    { label: "User Type", key: "userType" },
    { label: "Status", key: "status" },
  ];

  const purchaseColumns = [
    { label: "Invoice No", key: "invoiceNo" },
    { label: "Credits", key: "credits" },
    { label: "Amount", key: "amount" },
    { label: "Type", key: "type" },
    { label: "Paid At", key: "paidAt" },
  ];

  const monthItems = credits.purchasedThisMonth?.items || [];
  const purchaseData = monthItems.map((item, index) => ({
    id: item.invoiceNo || index,
    invoiceNo: item.invoiceNo || "--",
    credits: formatNumber(item.credits),
    amount: formatINR(item.totalAmount),
    type: item.type || "--",
    paidAt: formatDateTime(item.paidAt),
  }));

  return (
    <div className={styles["view-org__page"]}>
      {notification && (
        <div className={styles["view-org__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["view-org__header"]}>
        <div className={styles["view-org__title-area"]}>
          <Link
            to="/admin/organization"
            className={styles["view-org__back-btn"]}
            aria-label="Go Back"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
          </Link>
          <div className={styles["view-org__title-group"]}>
            <div className={styles["view-org__title-row"]}>
              <h2 className={styles["view-org__title"]}>
                {organization.companyName || organization.name || "Organization"}
              </h2>
              <span
                className={`${styles["view-org__badge"]} ${
                  isActive
                    ? styles["view-org__badge--active"]
                    : styles["view-org__badge--inactive"]
                }`}
              >
                {organization.status || "--"}
              </span>
            </div>
            <p className={styles["view-org__subtitle"]}>
              Owner: {organization.name || "--"} · {organization.email || "--"}
            </p>
          </div>
        </div>

        <button
          className={styles["view-org__edit-btn"]}
          onClick={() => navigate(`/admin/organization/edit/${organization.id}`)}
        >
          <Pencil size={16} strokeWidth={2.5} />
          <span>Edit</span>
        </button>
      </div>

      {/* KPI STAT CARDS */}
      <div className={styles["view-org__stats-grid"]}>
        <StatCard
          icon={<Wallet size={20} strokeWidth={2.5} />}
          variant="blue"
          label="Credit Balance"
          value={formatNumber(credits.balance ?? organization.balance, 2)}
          sub="Available credits"
        />
        <StatCard
          icon={<Gift size={20} strokeWidth={2.5} />}
          variant="purple"
          label="Gift Credits"
          value={formatNumber(credits.giftCreditsRemaining)}
          sub="Remaining"
        />
        <StatCard
          icon={<TrendingDown size={20} strokeWidth={2.5} />}
          variant="orange"
          label="This Month Spent"
          value={formatNumber(credits.spentThisMonth)}
          sub="Credits this month"
        />
        <StatCard
          icon={<TrendingDown size={20} strokeWidth={2.5} />}
          variant="red"
          label="Total Spent"
          value={formatNumber(credits.spentTotal)}
          sub="Credits all time"
        />
        <StatCard
          icon={<Users size={20} strokeWidth={2.5} />}
          variant="green"
          label="Users"
          value={formatNumber(organization.userCount)}
          sub={
            organization.unlimitedUsers
              ? "of unlimited"
              : `of ${formatNumber(organization.maxUsers)}`
          }
        />
      </div>

      {/* DETAIL SECTIONS */}
      <div className={styles["view-org__sections"]}>
        <Section
          icon={<User size={18} strokeWidth={2.5} />}
          title="Contact"
          fields={contactFields}
        />
        <Section
          icon={<Building2 size={18} strokeWidth={2.5} />}
          title="Company"
          fields={companyFields}
        />
        <Section
          icon={<Wallet size={18} strokeWidth={2.5} />}
          title="Billing & Audit"
          fields={billingFields}
        />
      </div>

      {/* LAST PURCHASE + THIS MONTH */}
      <div className={styles["view-org__sections"]}>
        <Section
          icon={<Receipt size={18} strokeWidth={2.5} />}
          title="Last Purchase"
          fields={
            lastPurchase
              ? [
                  { label: "Invoice No", value: lastPurchase.invoiceNo },
                  {
                    label: "Credits",
                    value: formatNumber(lastPurchase.credits),
                  },
                  {
                    label: "Amount",
                    value: formatINR(lastPurchase.totalAmount),
                  },
                  { label: "Type", value: lastPurchase.type },
                  {
                    label: "Paid At",
                    value: formatDateTime(lastPurchase.paidAt),
                  },
                ]
              : [{ label: "Last Purchase", value: "No purchases yet" }]
          }
        />
        <Section
          icon={<Receipt size={18} strokeWidth={2.5} />}
          title="Purchased This Month"
          fields={[
            {
              label: "Credits",
              value: formatNumber(credits.purchasedThisMonth?.credits),
            },
            {
              label: "Purchases",
              value: formatNumber(credits.purchasedThisMonth?.count),
            },
          ]}
        />
      </div>

      {/* THIS MONTH'S PURCHASES */}
      <div className={styles["view-org__table-card"]}>
        <div className={styles["view-org__table-header"]}>
          <h3 className={styles["view-org__section-title"]}>
            This Month's Purchases (
            {formatNumber(credits.purchasedThisMonth?.count)})
          </h3>
        </div>
        <APITable columns={purchaseColumns} data={purchaseData} />
      </div>

      {/* USERS TABLE */}
      <div className={styles["view-org__table-card"]}>
        <div className={styles["view-org__table-header"]}>
          <h3 className={styles["view-org__section-title"]}>
            Users ({formatNumber(organization.userCount)})
          </h3>
        </div>
        <APITable columns={userColumns} data={organization.users || []} />
      </div>
    </div>
  );
};

/* =========================
   SUBCOMPONENTS
========================= */
const StatCard = ({ icon, variant, label, value, sub }) => (
  <div className={styles["view-org__stat-card"]}>
    <div className={styles["view-org__stat-head"]}>
      <span className={styles["view-org__stat-label"]}>{label}</span>
      <div
        className={`${styles["view-org__stat-icon"]} ${
          styles[`view-org__stat-icon--${variant}`]
        }`}
      >
        {icon}
      </div>
    </div>
    <h3 className={styles["view-org__stat-value"]}>{value}</h3>
    {sub && <span className={styles["view-org__stat-sub"]}>{sub}</span>}
  </div>
);

const Section = ({ icon, title, fields }) => (
  <div className={styles["view-org__section"]}>
    <div className={styles["view-org__section-header"]}>
      <div className={styles["view-org__section-icon"]}>{icon}</div>
      <h3 className={styles["view-org__section-title"]}>{title}</h3>
    </div>

    <div className={styles["view-org__grid"]}>
      {fields.map((field, index) => (
        <div key={index} className={styles["view-org__field"]}>
          <span className={styles["view-org__label"]}>{field.label}</span>
          <span className={styles["view-org__value"]}>
            {field.value || field.value === 0 ? String(field.value) : "--"}
          </span>
        </div>
      ))}
    </div>
  </div>
);

export default AdminViewOrganization;
